import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../firebase/useAuth';
import { useMetadata } from '../../contexts/MetadataProvider';
import { useModal } from '../../context/ModalContext'; // Import useModal
import { fetchReviewWordsSessionItems } from '../../firebase/fetchReviewWordsSessionItems';
import { updateItemReview } from '../../firebase/reviewSession';
import { skipWord } from '../../firebase/words';
import { getSound } from '../../firebase/sounds';
import levelUpSoundFile from '../../assets/sounds/correct.mp3';
import levelDownSoundFile from '../../assets/sounds/wrong.wav';

// Components
import SessionLayout from '../../components/session/SessionLayout';
import InputSection from '../../components/session/InputSection';
import InteractiveCard from '../../components/session/InteractiveCard'; // Use generic InteractiveCard
import Spinner from '../../components/common/Spinner';
import { useToast } from '../../context/ToastContext';
import DetailsModal from '../../components/modals/DetailsModal/DetailsModal';
import ReportIssueModal from '../../components/modals/ReportIssueModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import SimpleAnswerSection from '../../components/session/SimpleAnswerSection';
import { AudioPlayerProvider } from '../../context/AudioPlayerContext';
import { checkWordAnswer } from '../../utils/answerCheck';
import { willEarnShieldToday } from '../../utils/streak';

const ReviewWordsSessionPage = () => {
  const { userId, userData, userLoading, emailVerified, subscriptionStatus } = useAuth();
  const { learnedWordIds, learnedSentenceIds, wordIdsLoading, sentenceIdsLoading, reviewedWordsData, reviewedSentencesData, learnedTodayData, loading: metadataLoading } = useMetadata();
  const { showModal, hideModal, modalStack } = useModal();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [reviewWords, setReviewWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [checkSubmitted, setCheckSubmitted] = useState(false);
  const [inputResults, setInputResults] = useState([]);
  const [lastProcessedIndex, setLastProcessedIndex] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [levelUpAlertState, setLevelUpAlertState] = useState({ show: false, level: '', type: '' }); // Renamed
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioFetchKey, setAudioFetchKey] = useState(0);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [ignoreClicked, setIgnoreClicked] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [wrapUpFlag, setWrapUpFlag] = useState(false);
  const [lastSkipped, setLastSkipped] = useState(false);
  const [seenCount, setSeenCount] = useState(0);
  const [userChoice, setUserChoice] = useState(null);
  const [correctWords, setCorrectWords] = useState([]);
  const [wrongWords, setWrongWords] = useState([]);
  const [markedWrongIds, setMarkedWrongIds] = useState(new Set());
  const [sessionSettings, setSessionSettings] = useState(() => {
    return (
      JSON.parse(localStorage.getItem('sessionSettings')) || {
        playCorrectSound: true,
        playWrongSound: true,
        showTranslation: true,
        inputMode: 'mixed',
        simpleAnswerMode: false,
        showFlippingAnimation: true,
        reducedAnimations: false,
        showKaraokeMode: true,
          autoPlayAfterAnswer: false,
          stayOnQuestionUntilCorrect: false,
      }
    );
  });
  const [pendingRetry, setPendingRetry] = useState(false);

  // Refs and Memos
  const inputRef = useRef(null);
  const initialFetchDone = useRef(false);
  const shieldEarnedRef = useRef(null); // whether this session will earn a 盾 (decided at start)
  const playCountsRef = useRef({}); // plays accumulated per item-id across attempts; recorded when the item is graded
  const levelUpSound = useMemo(() => new Audio(levelUpSoundFile), []);
  const levelDownSound = useMemo(() => new Audio(levelDownSoundFile), []);
  const currentWord = reviewWords[currentWordIndex];

  const isFullyLoading = loading || wordIdsLoading;

  useEffect(() => {
    if (userLoading) return; // Wait until auth state is confirmed

    if (userId && !emailVerified) {
      navigate('/dashboard', {
        state: {
          errorMessage: 'Please verify your email to start a session.',
        },
      });
    }
  }, [userLoading, userId, emailVerified, navigate]);

  useEffect(() => {
    if (location.state?.errorMessage) {
      showToast(location.state.errorMessage, 'error');
      // Clear the state to prevent the toast from reappearing on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showToast, navigate, location.pathname]);

  // --- Callbacks ---
  const handlePlayEnd = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleWordClick = useCallback(
    (word) => {
      showModal(<DetailsModal item={{ ...word }} handleClose={hideModal} />);
    },
    [showModal, hideModal]
  );

  const handleSeeAlsoClick = useCallback(() => {
    showModal(<DetailsModal item={currentWord} handleClose={hideModal} initialTab="seeAlso" />);
  }, [showModal, hideModal, currentWord]);

  const handleExamplesClick = useCallback(() => {
    showModal(<DetailsModal item={currentWord} handleClose={hideModal} initialTab="examples" />);
  }, [showModal, hideModal, currentWord]);

  const handleReportClick = useCallback(
    (currentUserInput) => {
      showModal(<ReportIssueModal item={currentWord} onClose={hideModal} userInput={currentUserInput} />);
    },
    [showModal, hideModal, currentWord]
  );

  const handleSkipConfirm = useCallback(() => {
    if (!currentWord) return;
    skipWord(userId, currentWord.id, subscriptionStatus.isSubscribed)
      .then((result) => { if (result?.status === 'already_skipped') showToast('This word is already skipped.', 'info'); })
      .catch(console.error);
    setLastProcessedIndex(currentWordIndex);
    // Skipping resolves the item as correct. If it had already been graded
    // wrong, retract that tally too — otherwise the item lands in both counters
    // and the header total overshoots the session size.
    if (sendResult === 'wrong') setWrongCount((p) => p - 1);
    if (sendResult !== 'correct') setCorrectCount((p) => p + 1);
    if (sessionSettings?.playCorrectSound) {
      levelUpSound.play();
    }
    setLevelUpAlertState({ show: true, level: 7, prevLevel: currentWord.srs_stage, type: 'up' });
    setCheckSubmitted(true);
    setIsCorrect(true);
    setLastSkipped(true);
    setUserChoice(true);
    setSendResult(null);
    setInputResults([{ correct: true, text: currentWord.word }]);
  }, [currentWord, userId, subscriptionStatus.isSubscribed, currentWordIndex, sendResult, sessionSettings?.playCorrectSound, levelUpSound, showToast]);

  const handleSkipClick = useCallback(() => {
    showModal(
      <ConfirmationModal
        type="skip"
        onClose={hideModal}
        onConfirm={() => {
          handleSkipConfirm();
          hideModal();
        }}
      />
    );
  }, [showModal, hideModal, handleSkipConfirm]);

  const handleCorrectReview = useCallback(() => {
    if (!currentWord) return;
    setUserChoice(true);
    setLastProcessedIndex(currentWordIndex);
    setCorrectCount((prev) => prev + 1);
    setSendResult('correct');
    if (sessionSettings?.playCorrectSound) levelUpSound.play();
    const newLevel = Math.min(currentWord.srs_stage + 1, 7);
    setLevelUpAlertState({ show: true, level: newLevel, prevLevel: currentWord.srs_stage, type: 'up' });
  }, [currentWord, currentWordIndex, sessionSettings, levelUpSound]);

  const handleCorrectWrongReview = useCallback(() => {
    if (!currentWord) return;
    setUserChoice(true);
    setLastProcessedIndex(currentWordIndex);
    setWrongCount((prev) => prev + 1);
    setSendResult('wrong');
    if (sessionSettings?.playWrongSound) levelDownSound.play();
    const newLevel = Math.max(currentWord.srs_stage - 1, 1);
    setLevelUpAlertState({ show: true, level: newLevel, prevLevel: currentWord.srs_stage, type: 'down' });
  }, [currentWord, currentWordIndex, sessionSettings, levelDownSound]);

  const handleWrongReview = useCallback(() => {
    setUserChoice(false);
    setMarkedWrongIds((prev) => new Set(prev).add(currentWord.id));
    setPendingRetry(true);
  }, [currentWord]);

  const handleCheck = useCallback(() => {
    if (!currentWord) return;
    const { input, isCorrect: isInputCorrect, expected } = checkWordAnswer(currentWord, userInput, sessionSettings?.inputMode || 'mixed');

    setInputResults([{ correct: isInputCorrect, text: input, ...(isInputCorrect ? {} : { expected }) }]);
    setCheckSubmitted(true);
    setIsCorrect(isInputCorrect);

    if (isInputCorrect && !markedWrongIds.has(currentWord.id)) {
      handleCorrectReview();
    } else if (isInputCorrect && markedWrongIds.has(currentWord.id)) {
      handleCorrectWrongReview();
    } else {
      handleWrongReview();
    }
  }, [currentWord, userInput, sessionSettings, handleCorrectReview, handleCorrectWrongReview, handleWrongReview, markedWrongIds]);

  const navigateToSessionEnd = useCallback(
    (finalCorrect, finalWrong) => {
      const sanitize = (word, isCorrect) => {
        const sanitized = { ...word, learned: true };
        sanitized.srs_stage = isCorrect ? Math.min(7, word.srs_stage + 1) : Math.max(1, word.srs_stage - 1);
        delete sanitized.word_id;
        delete sanitized.word_ref;
        return sanitized;
      };
      const sanitizedCorrect = finalCorrect.map((w) => sanitize(w, true));
      const sanitizedWrong = finalWrong.map((w) => sanitize(w, false));
      navigate('/session/end', {
        state: {
          correctWords: sanitizedCorrect,
          wrongWords: sanitizedWrong,
          type: 'words',
          shieldEarned: shieldEarnedRef.current,
        },
      });
    },
    [navigate]
  );

  const handleNextWord = useCallback(() => {
    const shouldStay = pendingRetry && sessionSettings?.stayOnQuestionUntilCorrect;
    setPendingRetry(false);
    if (shouldStay) {
      setCheckSubmitted(false);
      setInputResults([]);
      setIsCorrect(null);
      setUserChoice(null);
      setIgnoreClicked(false);
      setLastProcessedIndex(null);
      setSendResult(null);
      setAudioUrl(null);
      setAudioFetchKey((prev) => prev + 1);
      // Words are simply right or wrong — start the retry on a clean sheet
      // rather than preserving the wrong answer (unlike sentence sessions).
      setUserInput('');
      inputRef.current?.focus();
      return;
    }
    setLevelUpAlertState({ show: false, level: '', type: '' });
    setUserChoice(null);
    setAudioUrl(null);
    // Don't reset selectedVoice here - let the useEffect handle it based on the new word

    if (lastProcessedIndex !== null) {
      if (sendResult && currentWord) {
        const action = sendResult === 'correct' ? 'correct' : 'wrong';
        const playsBeforeSubmit = playCountsRef.current[currentWord.id] || 0;
        delete playCountsRef.current[currentWord.id];
        // Words have a male and female recording; their lengths are near-identical, so average the two.
        // `|| 0` guards a missing field (NaN), matching how a missing sentence duration falls back to 0.
        const soundDuration = (currentWord.sound_male_duration + currentWord.sound_female_duration) / 2 || 0;
        updateItemReview(userId, currentWord.id, 'words', action, subscriptionStatus.isSubscribed, (currentWord.correct || 0) + (currentWord.wrong || 0), playsBeforeSubmit, soundDuration).catch((error) => {
          if (error.code === 'already-reviewed') {
            showToast('This item was already reviewed in another session.', 'error');
          } else if (error.code === 'item-is-skipped') {
            showToast('This item has been skipped and cannot be reviewed.', 'error');
          } else if (error.code === 'vacation-mode') {
            showToast('Reviews cannot be submitted while vacation mode is active.', 'error');
          } else if (error.code === 'daily-limit-reached') {
            showToast('Daily review limit reached.', 'error');
          } else {
            console.error(error);
          }
        });
      }

      let newCorrect = correctWords;
      let newWrong = wrongWords;
      if (lastSkipped) {
        // Add the sentence with a 'skipped' flag
        newCorrect = [...correctWords, { ...currentWord, skipped: true }];
      } else if (sendResult === 'correct') {
        newCorrect = [...correctWords, currentWord];
      } else if (sendResult === 'wrong') {
        newWrong = [...wrongWords, currentWord];
      }

      setReviewWords((prev) => {
        const updated = prev.filter((_, index) => index !== lastProcessedIndex);
        let candidates = updated;
        if (wrapUpFlag) candidates = updated.filter((w) => w.seen);
        if (candidates.length === 0) {
          navigateToSessionEnd(newCorrect, newWrong);
          return [];
        }
        const nextIndexInCandidates = Math.floor(Math.random() * candidates.length);
        const originalIndexOfNext = updated.indexOf(candidates[nextIndexInCandidates]);
        setCurrentWordIndex(originalIndexOfNext);
        return updated;
      });

      setCorrectWords(newCorrect);
      setWrongWords(newWrong);
      setLastProcessedIndex(null);
      setSendResult(null);
      setLastSkipped(false);
    } else {
      setReviewWords((prev) => prev.map((w, i) => (i === currentWordIndex ? { ...w, seen: true } : w)));
      let candidates = wrapUpFlag ? reviewWords.filter((w) => w.seen) : reviewWords;
      const nextCandidates = candidates.filter((_, index) => reviewWords.indexOf(candidates[index]) !== currentWordIndex);
      if (nextCandidates.length > 0) {
        const nextIndexInCandidates = Math.floor(Math.random() * nextCandidates.length);
        const originalIndexOfNext = reviewWords.indexOf(nextCandidates[nextIndexInCandidates]);
        setCurrentWordIndex(originalIndexOfNext);
      }
    }

    setUserInput('');
    setInputResults([]);
    setCheckSubmitted(false);
    setIgnoreClicked(false);
    setIsCorrect(null);
    inputRef.current?.focus();
  }, [
    lastProcessedIndex,
    sendResult,
    currentWord,
    correctWords,
    wrongWords,
    lastSkipped,
    userId,
    subscriptionStatus.isSubscribed,
    wrapUpFlag,
    navigateToSessionEnd,
    reviewWords,
    currentWordIndex,
    showToast,
    pendingRetry,
    sessionSettings?.stayOnQuestionUntilCorrect,
  ]);

  const handleIgnoreButton = useCallback(() => {
    // Also check userChoice for simple answer mode
    if (userChoice === null || ignoreClicked) return;

    if (sendResult === 'correct') setCorrectCount((prev) => prev - 1);
    if (sendResult === 'wrong') setWrongCount((prev) => prev - 1);

    setIgnoreClicked(true);
    setLastProcessedIndex(null);
    setSendResult(null);
    setIsCorrect(null);
    setLevelUpAlertState({ show: false, level: '', type: '' });
    setUserChoice(null);
    // Ignoring discards this attempt — reset the plays so they don't carry over
    // and inflate "plays before answering" when the item is reviewed again.
    delete playCountsRef.current[currentWord.id];
    setMarkedWrongIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(currentWord.id);
      return newSet;
    });
  }, [userChoice, ignoreClicked, sendResult, currentWord]);

  const handleWrapUpSession = () => {
    setWrapUpFlag((prev) => {
      const isActivating = !prev;
      if (isActivating && currentWord) {
        setReviewWords((w) => w.map((item, index) => (index === currentWordIndex ? { ...item, seen: true } : item)));
      }
      return isActivating;
    });
  };

  const handleSettingsUpdate = (newSettings) => {
    if (newSettings.simpleAnswerMode !== sessionSettings.simpleAnswerMode && userChoice === null && !ignoreClicked) {
      setCheckSubmitted(false);
      setInputResults([]);
      setIsCorrect(null);
    }
    setSessionSettings(newSettings);
  };

  const handleSimpleModeChoice = (isCorrectChoice) => {
    if (userChoice !== null) return;
    setUserChoice(isCorrectChoice);
    setIsCorrect(isCorrectChoice);
    setInputResults([{ correct: isCorrectChoice, text: currentWord.word }]);
    if (isCorrectChoice) {
      if (currentWord && markedWrongIds.has(currentWord.id)) {
        handleCorrectWrongReview();
      } else {
        handleCorrectReview();
      }
    } else {
      handleWrongReview();
    }
    setCheckSubmitted(true);
  };

  // --- useEffect Hooks (Logic preserved) ---
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const message = 'You have unsaved changes. Are you sure you want to leave?';
      event.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => {
      if (e.touches?.[0]?.touchType === "stylus") return;
      let el = e.target;
      while (el && el !== document.body) {
        const { overflowY, overflowX } = window.getComputedStyle(el);
        if (
          ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) ||
          ((overflowX === "auto" || overflowX === "scroll") && el.scrollWidth > el.clientWidth)
        ) return;
        el = el.parentElement;
      }
      e.preventDefault();
    };
    document.addEventListener('touchmove', preventTouchMove, { passive: false });
    return () => document.removeEventListener('touchmove', preventTouchMove);
  }, []);

  // Determine initial voice for current word
  useEffect(() => {
    // Clear audio URL immediately when word changes
    setAudioUrl(null);

    if (currentWord && currentWord.sound_male && currentWord.sound_female) {
      // Set initial voice based on word's preference
      const initialVoice = currentWord.preferred_voice === 'random'
        ? (Math.random() < 0.5 ? 'male' : 'female')
        : (currentWord.preferred_voice || 'male');
      setSelectedVoice(initialVoice);
    } else {
      setSelectedVoice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWord?.id]); // Only run when word ID changes

  // Store current word ID to prevent stale closures
  const [currentWordId, setCurrentWordId] = useState(null);

  useEffect(() => {
    if (currentWord?.id !== currentWordId) {
      setCurrentWordId(currentWord?.id);
    }
  }, [currentWord?.id, currentWordId]);

  useEffect(() => {
    let isMounted = true;
    if (currentWord && currentWord.id === currentWordId) {
      if (currentWord.sound_male && currentWord.sound_female) {
        if (selectedVoice) {
          const soundFile = selectedVoice === 'male' ? currentWord.sound_male : currentWord.sound_female;
          getSound(soundFile, 'words').then(url => {
            if (isMounted && currentWord.id === currentWordId) setAudioUrl(url);
          }).catch(console.error);
        }
      } else if (currentWord.sound) {
        getSound(currentWord.sound, 'words').then(url => {
          if (isMounted && currentWord.id === currentWordId) setAudioUrl(url);
        }).catch(console.error);
      } else {
        setAudioUrl(null);
      }
    }
    return () => { isMounted = false; };
  }, [currentWord, selectedVoice, currentWordId, audioFetchKey]);

  useEffect(() => {
    setSeenCount(reviewWords.filter((w) => w.seen).length);
  }, [reviewWords]);

  useEffect(() => {
    // Decide at session start (before this session's activity) whether finishing
    // today will earn a new 盾, so the end screen can just read the result.
    if (shieldEarnedRef.current === null && !metadataLoading) {
      shieldEarnedRef.current = willEarnShieldToday({
        learnedTodayData,
        reviewedWordsData,
        reviewedSentencesData,
        userTimezone: userData?.settings?.timezone || 'UTC',
        vacationStartedAt: userData?.settings?.vacation_started_at,
        vacationPeriods: userData?.vacation_history,
      });
    }

    const fetchWords = async () => {
      if (!userId || !userData || initialFetchDone.current) return;
      // Claim the fetch before awaiting. This effect re-runs on every metadata
      // snapshot, and a snapshot landing mid-fetch would otherwise slip past
      // the guard and start a second full session load.
      initialFetchDone.current = true;
      setLoading(true);
      try {
        const wordsData = await fetchReviewWordsSessionItems(userId, userData, reviewedWordsData, subscriptionStatus.isSubscribed, learnedSentenceIds);
        if (wordsData.length === 0) {
          navigate('/dashboard', {
            state: { errorMessage: 'No words to review right now.' },
          });
          return;
        }
        setReviewWords(wordsData);
        setTotalCount(wordsData.length);
      } catch (error) {
        initialFetchDone.current = false; // release the claim so a later snapshot can retry
        console.error('Error fetching review words:', error);
        showToast('Failed to load session.', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (!userLoading && userId && userData && !subscriptionStatus.loading && !sentenceIdsLoading) {
      fetchWords();
    }
  }, [userId, userData, reviewedWordsData, reviewedSentencesData, learnedTodayData, metadataLoading, learnedSentenceIds, sentenceIdsLoading, navigate, userLoading, showToast, subscriptionStatus.loading, subscriptionStatus.isSubscribed]);

  useEffect(() => {
    if (!isFullyLoading && currentWord && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isFullyLoading, currentWord, checkSubmitted]);

  const isAnyModalOpen = modalStack.length > 0;
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key.toLowerCase() === 'i' && checkSubmitted && !isAnyModalOpen && handleIgnoreButton) {
        handleIgnoreButton();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [checkSubmitted, isAnyModalOpen, handleIgnoreButton]);


  const [isPenMode, setIsPenMode] = useState(false);
  useEffect(() => {
    const handlePointerDown = (e) => {
      if (e.pointerType === 'pen') {
        setIsPenMode(true);
      }
    };
    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  if ((isFullyLoading && reviewWords.length === 0) || !currentWord)
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <Spinner />
      </div>
    );

  return (
    <div className="h-dvh overflow-hidden">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 z-40">
          <SessionLayout
            updateSettings={handleSettingsUpdate}
            correctCount={correctCount}
            incorrectCount={wrongCount}
            totalCount={totalCount}
            onWrapUp={handleWrapUpSession}
            wrapUpFlag={wrapUpFlag}
            seenCount={seenCount}
          />
        </div>

        {/* Card area */}
        <div className={`${!sessionSettings.simpleAnswerMode && isPenMode ? 'flex-shrink flex-grow-0' : 'flex-1'} min-h-0 flex flex-col w-full max-w-4xl mx-auto`}>
          <AudioPlayerProvider
            audioSrc={audioUrl}
            onPlayEnd={handlePlayEnd}
            onPlay={() => { if (!checkSubmitted && currentWord) playCountsRef.current[currentWord.id] = (playCountsRef.current[currentWord.id] || 0) + 1; }}
            isAutoplay={!checkSubmitted}
            keepAwake={sessionSettings.keepAudioDeviceAwake}
          >
            <InteractiveCard
              currentItem={currentWord}
              isSubmitted={checkSubmitted}
              showFlippingAnimation={sessionSettings.showFlippingAnimation}
              isIgnored={ignoreClicked}
              isGraded={userChoice !== null}
              showTranslation={checkSubmitted && sessionSettings.showTranslation}
              answerResult={sendResult}
              onItemClick={handleWordClick}
              isHotkeysEnabled={!isAnyModalOpen}
              onIgnore={handleIgnoreButton}
              onSkip={handleSkipClick}
              onReport={handleReportClick}
              onSeeAlso={handleSeeAlsoClick}
              onExamples={handleExamplesClick}
              levelUpAlertState={levelUpAlertState}
              learnedWordIds={learnedWordIds}
              onVoiceChange={setSelectedVoice}
              currentVoice={selectedVoice}
              userInput={userInput}
              sessionSettings={sessionSettings}
              shrinkToContent={!sessionSettings.simpleAnswerMode && isPenMode}
            />
          </AudioPlayerProvider>
        </div>

        {/* Input section */}
        <div className="flex-shrink-0 w-full bg-background-secondary border-t border-border z-50">
          <div className="w-full max-w-4xl mx-auto p-4 session-input-area">
            {sessionSettings.simpleAnswerMode ? (
              <SimpleAnswerSection
                showResultCard={checkSubmitted}
                onReveal={() => {
                  setCheckSubmitted(true);
                }}
                onChoice={handleSimpleModeChoice}
                userChoice={userChoice}
                isIgnored={ignoreClicked}
                onNext={handleNextWord}
              />
            ) : (
              <InputSection
                ref={inputRef}
                checkSubmitted={checkSubmitted}
                userInput={userInput}
                inputResults={inputResults}
                setUserInput={setUserInput}
                handleCheck={handleCheck}
                handleNext={handleNextWord}
                isCorrect={isCorrect}
                isIgnored={ignoreClicked}
                isAnyModalOpen={isAnyModalOpen}
                inputMode={sessionSettings?.inputMode || 'mixed'}
                autoConvertToKana={sessionSettings?.autoConvertToKana ?? true}
                reducedAnimations={sessionSettings?.reducedAnimations}
              />
            )}
          </div>
        </div>

        {/* Palm rest spacer: only in pen mode */}
        {!sessionSettings.simpleAnswerMode && isPenMode && <div className="flex-1 min-h-0 pointer-events-none" />}
      </div>
    </div>
  );
};

export default ReviewWordsSessionPage;
